import React, { useState, useEffect } from "react";
import axios from "axios";
import TableContainer from "Common/TableContainer";
import {
  Factory,
  Users,
  Coins,
  Wallet,
  Truck,
  FileText,
  ClipboardList,
} from "lucide-react";

interface Factory {
  id: number;
  factory_name: string;
}

interface FullReport {
  factory_id: number;
  factory_name: string;
  total_money_received_factory: number;
  total_farmers_debt: number;
  total_farmers_payment_cash: number;
  total_farmers_payment_seed: number;
  total_payment_farmers_pesticide: number;
  remained_farmers_debt: number;
  remained_now_contractor: number;
  factory_delivered_tonnage: number;
  factory_commitment_tonnage: number;
  remaining_commitment_tonnage: number;
}

interface FullReportResponse {
  reports: FullReport[];
  total_money_received_all_factories: number;
  total_farmers_debt_all: number;
  total_farmers_payment_cash_all: number;
  total_farmers_payment_seed_all: number;
  total_payment_farmers_pesticide_all: number;
  remained_farmers_debt_all: number;
  remained_now_contractor_all: number;
  total_factory_delivered_tonnage: number;
  total_factory_commitment_tonnage: number;
  total_remaining_commitment_tonnage: number;
  sum_farmers_load: number;
  sum_farmers_seed: number;
  sum_farmers_pesticide: number;
}

const FactoryReportPage: React.FC = () => {
  const [factories, setFactories] = useState<Factory[]>([]);
  const [selectedFactoryId, setSelectedFactoryId] = useState<number | null>(
    null
  );
  const [reportData, setReportData] = useState<
    FullReport | FullReportResponse | null
  >(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const cropYearID = localStorage.getItem("crop_year_id");
  const [YearCoptSelect, setYearCoptSelect] = useState<any>();
  useEffect(() => {
    const featchData = async () => {
      const response = await axios.get("https://api.havirkesht.ir/crop-year/");
      const getyear = response?.data?.items?.find(
        (items: any) => items.id == cropYearID
      );
      setYearCoptSelect(getyear?.crop_year_name);
    };
    featchData();
  }, []);
  // Fetch report when factory is selected
  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError(null);

      try {
        const app_atkey = localStorage.getItem("app_atkey");
        if (!app_atkey) {
          throw new Error("No authentication token found");
        }
        const cropID = Number(localStorage.getItem("crop_year_id"));
        const response = await axios.post(
          `https://api.havirkesht.ir/report-factory/?crop_year_id=${cropID}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${app_atkey}`,
              "Content-Type": "application/json",
            },
          }
        );

        setReportData(response.data);
      } catch (err) {
        setError("خطا در دریافت گزارش");
        console.error("Error fetching report:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [selectedFactoryId, factories]);

  const handleFactoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedFactoryId(value === "null" ? null : parseInt(value, 10));
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined) return "۰";
    return new Intl.NumberFormat("fa-IR").format(value) + " ";
  };

  const isFullReport = (data: any): data is FullReportResponse => {
    return data?.reports !== undefined;
  };

  // Card component for consistent styling
  const StatCard = ({ title, value }: { title: string; value: string }) => (
    <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700 shadow-sm hover:shadow-md transition-shadow">
      <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300">
        {title}
      </h3>
      <p className="text-base font-semibold mt-1 dark:text-white">{value}</p>
    </div>
  );

  return (
    <div className="container mx-auto p-4 dark:bg-gray-900 min-h-screen">
      {loading && (
        <div className="text-center py-4 dark:text-gray-300">
          در حال دریافت داده‌ها...
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-right dark:bg-red-900 dark:border-red-700 dark:text-red-100">
          {error}
        </div>
      )}

      {reportData && (
        <div className="bg-white shadow-md rounded-lg p-6 dark:bg-gray-800">
          {isFullReport(reportData) ? (
            <>
              <h2 className="text-xl font-semibold mb-4 text-right dark:text-white">
                {` گزارش کلی همه کارخانه‌ها براساس سال زراعی ${YearCoptSelect}`}
              </h2>

              <div className="mb-8">
                <h3 className="font-bold text-lg mb-3 text-right dark:text-gray-300">
                  جمع کل:
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50/50 dark:bg-gray-800/30 rounded-lg">
                  <div className="rounded-lg p-4 shadow-sm bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium opacity-90">
                          کل مبلغ دریافتی از کارخانه‌ها
                        </p>
                        <p className="text-xl font-bold mt-1">
                          {formatCurrency(
                            reportData.total_money_received_all_factories
                          )}
                        </p>
                      </div>
                      <div className="p-2 rounded-full bg-white/20">
                        <Factory className="w-5 h-5" />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg p-4 shadow-sm bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium opacity-90">
                          کل طلب کشاورزان
                        </p>
                        <p className="text-xl font-bold mt-1">
                          {formatCurrency(reportData.total_farmers_debt_all)}
                        </p>
                      </div>
                      <div className="p-2 rounded-full bg-white/20">
                        <Users className="w-5 h-5" />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg p-4 shadow-sm bg-gradient-to-r from-green-500 to-green-600 text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium opacity-90">
                          کل پرداختی نقدی
                        </p>
                        <p className="text-xl font-bold mt-1">
                          {formatCurrency(
                            reportData.total_farmers_payment_cash_all
                          )}
                        </p>
                      </div>
                      <div className="p-2 rounded-full bg-white/20">
                        <Coins className="w-5 h-5" />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg p-4 shadow-sm bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium opacity-90">
                          مانده حساب پیمانکار
                        </p>
                        <p className="text-xl font-bold mt-1">
                          {formatCurrency(
                            reportData.remained_now_contractor_all
                          )}
                        </p>
                      </div>
                      <div className="p-2 rounded-full bg-white/20">
                        <Wallet className="w-5 h-5" />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg p-4 shadow-sm bg-gradient-to-r from-teal-500 to-teal-600 text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium opacity-90">
                          کل تناژ تحویلی به کارخانجات
                        </p>
                        <p className="text-xl font-bold mt-1">
                          {formatCurrency(
                            reportData.total_factory_delivered_tonnage
                          )}
                        </p>
                      </div>
                      <div className="p-2 rounded-full bg-white/20">
                        <Truck className="w-5 h-5" />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg p-4 shadow-sm bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium opacity-90">
                          کل تناژ تعهد به کارخانجات
                        </p>
                        <p className="text-xl font-bold mt-1">
                          {formatCurrency(
                            reportData.total_factory_commitment_tonnage
                          )}
                        </p>
                      </div>
                      <div className="p-2 rounded-full bg-white/20">
                        <FileText className="w-5 h-5" />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg p-4 shadow-sm bg-gradient-to-r from-rose-500 to-rose-600 text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium opacity-90">
                          کل مانده تعهد به کارخانجات
                        </p>
                        <p className="text-xl font-bold mt-1">
                          {formatCurrency(
                            reportData.total_remaining_commitment_tonnage
                          )}
                        </p>
                      </div>
                      <div className="p-2 rounded-full bg-white/20">
                        <ClipboardList className="w-5 h-5" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="font-bold text-lg mb-3 text-right dark:text-gray-300">
                جزئیات هر کارخانه:
              </h3>
              {reportData && reportData.reports.length > 0 ? (
                <TableContainer
                  isPagination={true}
                  columns={[
                    {
                      header: "نام کارخانه",
                      accessorKey: "factory_name",
                      cell: (cell: any) => (
                        <span className="dark:text-gray-200">
                          {cell.getValue()}
                        </span>
                      ),
                    },
                    {
                      header: "واریزی کارخانه",
                      accessorKey: "total_money_received_factory",
                      cell: (cell: any) => (
                        <span className="dark:text-gray-200">
                          {formatCurrency(cell.getValue())}
                        </span>
                      ),
                    },
                    {
                      header: " جمع مبلغ بار کشاورزان",
                      accessorKey: "sum_farmers_load",
                      cell: (cell: any) => (
                        <span className="dark:text-gray-200">
                          {formatCurrency(cell.getValue())}
                        </span>
                      ),
                    },
                    {
                      header: " جمع مبلغ  بذر کشاورزان",
                      accessorKey: "sum_farmers_seed",
                      cell: (cell: any) => (
                        <span className="dark:text-gray-200">
                          {formatCurrency(cell.getValue())}
                        </span>
                      ),
                    },
                    {
                      header: " جمع مبلغ  سم کشاورزان",
                      accessorKey: "sum_farmers_pesticide ",
                      cell: (cell: any) => (
                        <span className="dark:text-gray-200">
                          {formatCurrency(cell.getValue())}
                        </span>
                      ),
                    },
                    {
                      header: "مانده تا تسویه",
                      accessorKey: "factory_remaining_to_settlement",
                      cell: (cell: any) => (
                        <span className="dark:text-gray-200">
                          {formatCurrency(cell.getValue())}
                        </span>
                      ),
                    },
                    {
                      header: "تناژ تحویلی به کارخانه",
                      accessorKey: "factory_delivered_tonnage",
                      cell: (cell: any) => (
                        <span className="dark:text-gray-200">
                          {formatCurrency(cell.getValue())}
                        </span>
                      ),
                    },
                    {
                      header: "تناژ تعهد به کارخانه",
                      accessorKey: "factory_commitment_tonnage",
                      cell: (cell: any) => (
                        <span className="dark:text-gray-200">
                          {formatCurrency(cell.getValue())}
                        </span>
                      ),
                    },
                    {
                      header: "مانده تناژ تا تعهد",
                      accessorKey: "remaining_commitment_tonnage",
                      cell: (cell: any) => (
                        <span className="dark:text-gray-200">
                          {formatCurrency(cell.getValue())}
                        </span>
                      ),
                    },
                  ]}
                  isGlobalFilter={true}
                  customnumberfortable={10}
                  data={reportData.reports}
                  customPageSize={10}
                  divclassName="-mx-5 overflow-x-auto"
                  tableclassName="w-full whitespace-nowrap"
                  theadclassName="ltr:text-left rtl:text-right bg-gray-100 dark:bg-gray-600"
                  thclassName="px-3 py-2 border text-right text-sm font-medium dark:text-gray-200"
                  tdclassName="px-3 py-2 border text-right text-sm dark:text-gray-800 hover:bg-gray-50 dark:hover:bg-gray-600"
                  PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
                />
              ) : null}
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold mb-4 text-right dark:text-white">
                گزارش کارخانه: {reportData.factory_name}
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <StatCard
                  title="کل مبلغ دریافتی از کارخانه"
                  value={formatCurrency(
                    reportData.total_money_received_factory
                  )}
                />
                <StatCard
                  title="كل طلب کشاورزان"
                  value={formatCurrency(reportData.total_farmers_debt)}
                />
                <StatCard
                  title="کل پرداختی نقدی به کشاورزان"
                  value={formatCurrency(reportData.total_farmers_payment_cash)}
                />
                <StatCard
                  title="کل پرداختی بابت بذر به کشاورزان"
                  value={formatCurrency(reportData.total_farmers_payment_seed)}
                />
                <StatCard
                  title="کل پرداختی بابت سم به کشاورزان"
                  value={formatCurrency(
                    reportData.total_payment_farmers_pesticide
                  )}
                />
                <StatCard
                  title="مانده طلب کشاورزان"
                  value={formatCurrency(reportData.remained_farmers_debt)}
                />
                <StatCard
                  title="مانده فعلی در حساب پیمانکار"
                  value={formatCurrency(reportData.remained_now_contractor)}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FactoryReportPage;
